import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import { cn } from '@/lib/cn';
import { fmtInt, fmtUsd } from '@/lib/format';
import type { OverviewResponse } from '@/lib/api-types';

export function SuperHero({
  data,
  isLoading,
}: {
  data: OverviewResponse | undefined;
  isLoading: boolean;
}) {
  return (
    <section
      aria-label="Pipeline at a glance"
      className="relative -mx-4 overflow-hidden rounded-none bg-sidebar text-white shadow-card sm:-mx-6 lg:-mx-10 lg:rounded-2xl"
      data-testid="superhero"
    >
      {/* Atmospherics — teal/emerald radials */}
      <div
        className="pointer-events-none absolute inset-0"
        aria-hidden
        style={{
          backgroundImage:
            'radial-gradient(circle at 85% 15%, rgba(45,212,191,0.35), transparent 45%), radial-gradient(circle at 15% 85%, rgba(20,184,166,0.25), transparent 50%), radial-gradient(circle at 50% 50%, rgba(15,118,110,0.18), transparent 60%)',
        }}
      />
      {/* Grid mesh */}
      <svg
        className="pointer-events-none absolute inset-0 h-full w-full opacity-[0.08]"
        viewBox="0 0 1400 800"
        preserveAspectRatio="xMidYMid slice"
        fill="none"
        aria-hidden
      >
        <defs>
          <pattern id="superhero-grid" width="32" height="32" patternUnits="userSpaceOnUse">
            <path d="M 32 0 L 0 0 0 32" fill="none" stroke="white" strokeWidth="0.5" />
          </pattern>
        </defs>
        <rect width="1400" height="800" fill="url(#superhero-grid)" />
        <path
          d="M -40 650 L 340 430 L 220 220 L 740 40"
          stroke="rgba(94,234,212,0.6)"
          strokeWidth="1.4"
          fill="none"
        />
        <path
          d="M 1400 700 L 920 520 L 1060 320 L 720 120"
          stroke="rgba(94,234,212,0.35)"
          strokeWidth="1.2"
          fill="none"
        />
      </svg>

      {/* Top strip — brand + utility */}
      <div className="relative flex items-center justify-between border-b border-white/10 px-6 py-4 sm:px-10">
        <div className="flex items-center gap-2.5">
          <span className="relative flex h-2 w-2">
            <span className="absolute inset-0 animate-ticker rounded-full bg-brand-300" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-brand-300" />
          </span>
          <span className="text-[0.6875rem] font-semibold uppercase tracking-[0.14em] text-brand-200">
            Federal lease intelligence · Live
          </span>
        </div>
        <div className="hidden items-center gap-5 text-[0.75rem] text-white/70 sm:flex">
          <Link to="/rlps" className="inline-flex items-center gap-1 hover:text-white">
            Solicitations <ArrowUpRight className="h-3 w-3" />
          </Link>
          <Link to="/forms" className="inline-flex items-center gap-1 hover:text-white">
            Forms <ArrowUpRight className="h-3 w-3" />
          </Link>
          <Link to="/agents" className="inline-flex items-center gap-1 hover:text-white">
            Agent ledger <ArrowUpRight className="h-3 w-3" />
          </Link>
        </div>
      </div>

      {/* Hero body */}
      <div className="relative grid grid-cols-1 gap-8 px-6 py-14 sm:px-10 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)] lg:gap-10 lg:px-14 lg:py-20 xl:py-24">
        <div className="flex flex-col gap-8">
          <h1 className="text-[2.25rem] font-bold leading-[1.05] tracking-head text-white sm:text-[2.75rem] lg:text-[3.25rem] xl:text-[3.75rem]">
            Every federal RLP,
            <br />
            <span className="bg-gradient-to-r from-brand-300 via-brand-200 to-white bg-clip-text text-transparent">
              read, ranked, and ready to act on.
            </span>
          </h1>
          <div className="flex flex-wrap items-center gap-3">
            <Link
              to="/rlps"
              className="inline-flex h-11 items-center gap-2 rounded-lg bg-brand-500 px-5 text-[0.9375rem] font-semibold text-white shadow-card transition-colors hover:bg-brand-400"
              data-testid="hero-primary-cta"
            >
              Browse solicitations <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/forms"
              className="inline-flex h-11 items-center gap-2 rounded-lg border border-white/20 bg-white/5 px-5 text-[0.9375rem] font-semibold text-white transition-colors hover:bg-white/10"
              data-testid="hero-secondary-cta"
            >
              Review drafts
            </Link>
          </div>
        </div>

        <div className="flex flex-col justify-end gap-4 lg:pl-10">
          <p className="max-w-md text-[0.9375rem] leading-relaxed text-white/70 lg:text-right lg:ml-auto">
            RLPScout watches SAM.gov around the clock, extracts the requirements the moment a
            notice drops, sources candidate properties, and drafts broker outreach — so you
            only read signal, never noise.
          </p>
        </div>
      </div>

      {/* Stats strip */}
      <div className="relative border-t border-white/10 bg-black/20 backdrop-blur-sm">
        <div className="grid grid-cols-1 divide-white/10 sm:grid-cols-2 sm:divide-x lg:grid-cols-4">
          <StatCell kicker="Results that matter" label="" value="" isHeader />
          <StatCell
            label="Active solicitations under watch"
            value={isLoading ? '—' : fmtInt(data?.active_rlps)}
            suffix="open"
          />
          <StatCell
            label="Drafts awaiting your signature"
            value={isLoading ? '—' : fmtInt(data?.pending_approvals)}
            suffix="ready"
            accent={data && data.pending_approvals > 0}
          />
          <StatCell
            label={
              data
                ? `of ${fmtUsd(data.month_budget_usd)} monthly agent budget`
                : 'Monthly agent budget used'
            }
            value={isLoading ? '—' : fmtUsd(data?.month_cost_usd)}
            suffix="spent"
          />
        </div>
      </div>
    </section>
  );
}

function StatCell({
  kicker,
  label,
  value,
  suffix,
  accent,
  isHeader,
}: {
  kicker?: string;
  label: string;
  value: string;
  suffix?: string;
  accent?: boolean;
  isHeader?: boolean;
}) {
  if (isHeader) {
    return (
      <div className="hidden items-center px-8 py-6 lg:flex">
        <span className="text-[0.625rem] font-semibold uppercase tracking-[0.18em] text-white/55">
          {kicker}
        </span>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-2 px-6 py-6 sm:px-8">
      <div className="flex items-baseline gap-2">
        <div
          className={cn(
            'font-bold leading-none tracking-head text-white',
            'text-[2.5rem] sm:text-[3rem] lg:text-[3.25rem]',
            accent && 'text-brand-200'
          )}
        >
          {value}
        </div>
        {suffix && (
          <div className="text-[0.75rem] font-semibold uppercase tracking-[0.12em] text-white/55">
            {suffix}
          </div>
        )}
      </div>
      <div className="text-[0.8125rem] leading-snug text-white/70">{label}</div>
    </div>
  );
}
